import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import ReCaptcha from 'react-google-recaptcha'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import * as z from 'zod'

import { Button, buttonVariants } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { emailSchema } from '@/types/schemas.ts'
import { AppRoutes, CAPTCHA_SITE_KEY } from '@/utils/constants'
import { handleError } from '@/utils/error'

import { useAccount } from '../hooks/useAccounts'

export type ResetPasswordMailFormValues = z.infer<
  typeof resetPasswordMailFormSchema
>

const resetPasswordMailFormSchema = z.object({
  email: emailSchema,
  'g-recaptcha-response': z.string().min(1),
})

export const ResetPasswordMail = () => {
  const navigate = useNavigate()
  const form = useForm<ResetPasswordMailFormValues>({
    mode: 'onChange',
    resolver: zodResolver(resetPasswordMailFormSchema),
    defaultValues: {
      email: '',
      'g-recaptcha-response': '',
    },
  })

  const { handleSubmit } = form
  const { resetPasswordMail } = useAccount()
  const toast = useToast()

  const onSubmit = useCallback(
    async (values: ResetPasswordMailFormValues) => {
      try {
        await resetPasswordMail.mutateAsync({
          email: values.email,
        })
        navigate(AppRoutes.Auth.Login.asUrl())
        toast.success(
          'Please check your email for the password reset link. If you do not see it, please check your spam folder.',
        )
      } catch (error) {
        handleError(error)
      }
    },
    [resetPasswordMail, toast, navigate],
  )

  return (
    <div>
      <FormProvider {...form}>
        <form
          className="grid gap-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid gap-4">
            <FormField
              name="email"
              label="Email Address"
              placeholder="mail@example.com"
            />

            <Controller
              control={form.control}
              name="g-recaptcha-response"
              render={({ field: { onChange } }) => (
                <ReCaptcha
                  sitekey={CAPTCHA_SITE_KEY}
                  onChange={(value) => {
                    if (!value) {
                      return
                    }

                    onChange(value)
                  }}
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            <Link
              to={AppRoutes.Auth.Login.asUrl()}
              className={buttonVariants({
                variant: 'outline',
                className: 'w-full',
              })}
            >
              Back
            </Link>
            <Button
              disabled={!form.formState.isDirty || !form.formState.isValid}
              className="w-full"
            >
              Reset Password
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
